import { useInfiniteQuery } from '@tanstack/react-query';
import { Doc, SharedDriveModel } from '../../../../../types';
import DriveFolderTree from './drive-folder-tree';
import SharedDrivesChildMoveDialogContent from './shared-drives/shared-drives-child/shared-drives-child-move-dialog-content';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { CallbackDoc } from 'react-google-drive-picker/dist/typeDefs';
import { getDriveFoldersForTab, UserKeys } from '../../../../api/users-api';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

interface DriveListDialogContentProps {
  selectedTab: string;
  userEmail: string;
  driveId: string;
  folder: string;
  setFolder: Dispatch<SetStateAction<string>>;
  selectedDocs: Doc[];
  autocompleteValue: string;
  setAutocompleteValue: Dispatch<SetStateAction<string>>;
  selectedDrive: SharedDriveModel | null;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  selectedDrivePickerFolder: CallbackDoc | null;
  setSelectedDrivePickerFolder: Dispatch<SetStateAction<CallbackDoc | null>>;
  selectedPickerFolderIsInDocs: boolean;
  openChangeLogDialog: boolean;
  setOpenChangeLogDialog: Dispatch<SetStateAction<boolean>>;
  changeLogMessage: string;
  setChangeLogMessage: Dispatch<SetStateAction<string>>;
  mutate: (driveOrFolderId: string) => void;
  isMoving: boolean;
  openPermissionCheckDialog: boolean;
  setOpenPermissionCheckDialog: Dispatch<SetStateAction<boolean>>;
  changeLogRoles: string[];
  setPermissionCheckLoading: Dispatch<SetStateAction<boolean>>;
}

export default function DriveListDialogContent({
  selectedTab,
  userEmail,
  driveId,
  folder,
  setFolder,
  selectedDocs,
  autocompleteValue,
  setAutocompleteValue,
  selectedDrive,
  setSelectedDrive,
  selectedDrivePickerFolder,
  setSelectedDrivePickerFolder,
  selectedPickerFolderIsInDocs,
  openChangeLogDialog,
  setOpenChangeLogDialog,
  changeLogMessage,
  setChangeLogMessage,
  mutate,
  isMoving,
  openPermissionCheckDialog,
  setOpenPermissionCheckDialog,
  changeLogRoles,
  setPermissionCheckLoading,
}: DriveListDialogContentProps) {
  const {
    data: allFolders,
    fetchNextPage: fetchNextPageDrive,
    isFetching: isFetchingFolderTree,
    isLoading: isLoadingFolderTree,
  } = useInfiniteQuery({
    queryKey: [UserKeys.DriveFolders, userEmail],
    queryFn: ({ pageParam }) => {
      return getDriveFoldersForTab(
        userEmail,
        pageParam || '',
        false,
        false,
        false,
        false,
        'name'
      );
    },
    initialPageParam: driveId,
    getNextPageParam: () => {
      return folder;
    },
    enabled:
      driveId !== undefined &&
      driveId !== '' &&
      folder !== '' &&
      folder !== 'shared-drives',
  });

  useEffect(() => {
    if (
      folder !== '' &&
      !isFetchingFolderTree &&
      !isLoadingFolderTree &&
      !allFolders?.pageParams.includes(folder)
    ) {
      fetchNextPageDrive();
    }
  }, [
    allFolders?.pageParams,
    fetchNextPageDrive,
    folder,
    isFetchingFolderTree,
    isLoadingFolderTree,
  ]);

  return (
    <>
      {selectedTab === 'drive' && (
        <Box sx={{ p: 1, maxHeight: 250, overflowY: 'auto' }}>
          <DriveFolderTree
            userEmail={userEmail}
            driveId={driveId}
            selectedFolder={folder}
            setSelectedFolder={setFolder}
            allFolders={allFolders}
            isLoading={isLoadingFolderTree}
            displayJustDrive={true}
            disabledNodes={selectedDocs.map((doc) => doc.id)}
          />
        </Box>
      )}
      {selectedTab === 'sharedDrives' && (
        <Stack sx={{ mt: 2 }}>
          <SharedDrivesChildMoveDialogContent
            autocompleteValue={autocompleteValue}
            setAutocompleteValue={setAutocompleteValue}
            selectedDrive={selectedDrive}
            setSelectedDrive={setSelectedDrive}
            selectedDrivePickerFolder={selectedDrivePickerFolder}
            setSelectedDrivePickerFolder={setSelectedDrivePickerFolder}
            selectedFolder={folder}
            selectedPickerFolderIsInDocs={selectedPickerFolderIsInDocs}
            openChangeLogDialog={openChangeLogDialog}
            setOpenChangeLogDialog={setOpenChangeLogDialog}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            isMoving={isMoving}
            openPermissionCheckDialog={openPermissionCheckDialog}
            setOpenPermissionCheckDialog={setOpenPermissionCheckDialog}
            changeLogRoles={changeLogRoles}
            setPermissionCheckLoading={setPermissionCheckLoading}
          />
        </Stack>
      )}
    </>
  );
}
